import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/shadcn/card'
import { CardUI, CardData } from '../types'
import { Percentage } from './Percentage'
import { RadialProgress } from './RadialProgress'
import { Text } from './Text'
import { KeyValue } from './KeyValue'
import { Link, useSearchParams } from 'react-router'
import { CATALOG_BASE_PATH } from '@/const'
import clsx from 'clsx'
import { useSubmitPageFilters } from '@/catalog-apps/components/AppPage2/Filter/useSubmitPageFilters'
import { ChartRadialContent } from '../../ChartRadial/ChartRadialContent'
import { BadgeCard } from './BadgeCard'
import { MarkdownCard } from './MarkdownCard'
import { ProgressCard } from './ProgressCard'
import { ChartBarContent } from '../../ChartBar/ChartBarContent'
import { ChartPieContent } from '../../ChartPie/ChartPieContent'
import { ChartRadarContent } from '../../ChartRadar/ChartRadarContent'
import { getBorderLeft, getIconColor, getBadgeColor } from '../utils'
import { IconBg } from '@/components/ui/IconBg'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/shadcn/badge'
import { CardAction } from '../CardAction'

export function SingleCard({ card, data }: { card: CardUI; data: CardData }) {
  const { submitFilters } = useSubmitPageFilters()
  const [searchParams] = useSearchParams()
  const { navigation, action } = card

  const headerVisible = card.title || card.styled?.icon || data.headerStatus
  const footerVisible = card.footerText || data.footerStatus
  const hasIcon = !!card.styled?.icon
  const hasVisibleAction = action?.isVisible === true
  const isCardClickable = hasVisibleAction ? !action.isDisabled : !!navigation

  const cardHeaderContent = (
    <>
      <div className="flex flex-col">
        <CardTitle className="text-base font-bold">{card.title}</CardTitle>
        {card.description && <CardDescription className="text-xs leading-tight">{card.description}</CardDescription>}
      </div>
      {data.headerStatus && (
        <Badge className={cn(getBadgeColor(data.headerStatus?.statusType), 'ml-auto mr-0 text-sm font-bold shadow')}>
          {data.headerStatus?.statusText}
        </Badge>
      )}
    </>
  )

  const cardContent = (
    <Card
      className={clsx('group flex h-full flex-col transition-all duration-300', {
        'bg-offwhite shadow-md': card.config.isBoxed,
        'ring-0': !card.config.isBoxed,
        'hover:bg-background': isCardClickable,
        'gap-0': card.config.type === 'radial-chart',
        'pb-0': footerVisible,
        [getBorderLeft(card.styled?.color)]: !!card.styled?.color,
      })}
    >
      <CardHeader className={headerVisible ? '' : 'hidden'}>
        <div className="flex items-center gap-5">
          {hasIcon ? (
            <>
              <IconBg className={cn(getIconColor(card.styled?.color), 'size-12 text-white')}>
                <DynamicIcon iconName={card.styled?.icon ?? ''} className="size-8" />
              </IconBg>
              {cardHeaderContent}
            </>
          ) : (
            cardHeaderContent
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {card.config.type === 'radial' ? (
          <RadialProgress config={card.config} data={data} />
        ) : card.config.type === 'percentage' ? (
          <Percentage config={card.config} data={data} />
        ) : card.config.type === 'text' ? (
          <Text config={card.config} data={data} />
        ) : card.config.type === 'key-value' ? (
          <KeyValue config={card.config} data={data} />
        ) : card.config.type === 'badge-card' ? (
          <BadgeCard config={card.config} data={data} />
        ) : card.config.type === 'markdown-card' ? (
          <MarkdownCard config={card.config} data={data} />
        ) : card.config.type === 'progress' ? (
          <ProgressCard config={card.config} data={data} />
        ) : card.config.type === 'radial-chart' ? (
          <div
            className={clsx(
              'rounded-full bg-[radial-gradient(circle,white_0%,white_30%,transparent_70%)] dark:bg-[radial-gradient(circle,black_0%,black_30%,transparent_70%)]',
              {
                'group-hover:bg-none': isCardClickable,
              }
            )}
          >
            <ChartRadialContent
              uiSchema={{ chartConfig: card.config.chartConfig }}
              dataSchema={{ data: data.data ?? [] }}
            />
          </div>
        ) : card.config.type === 'bar-chart' ? (
          <ChartCardWrapper
            clickable={isCardClickable}
            className={card.config.chartConfig.orientation === 'horizontal' ? 'min-h-[450px]' : ''}
          >
            <ChartBarContent
              uiSchema={{ chartConfig: card.config.chartConfig }}
              dataSchema={{ data: data.data ?? [] }}
            />
          </ChartCardWrapper>
        ) : card.config.type === 'pie-chart' ? (
          <ChartCardWrapper clickable={isCardClickable}>
            <ChartPieContent
              uiSchema={{ chartConfig: card.config.chartConfig }}
              dataSchema={{ data: data.data ?? [] }}
            />
          </ChartCardWrapper>
        ) : card.config.type === 'radar-chart' ? (
          <ChartCardWrapper clickable={isCardClickable}>
            <ChartRadarContent
              uiSchema={{ chartConfig: card.config.chartConfig }}
              dataSchema={{ data: data.data ?? [] }}
            />
          </ChartCardWrapper>
        ) : (
          <pre>{JSON.stringify({ config: card.config, data }, null, 2)}</pre>
        )}
      </CardContent>
      <CardFooter className={clsx('bg-transparent', { hidden: !footerVisible })}>
        <div className="flex flex-1 items-center justify-between gap-2">
          {card.footerText}
          <Badge className={cn(getBadgeColor(data.footerStatus?.statusType), 'ml-auto mr-0 text-sm font-bold shadow')}>
            {data.footerStatus?.statusText}
            {data.footerStatus?.icon && <DynamicIcon iconName={data.footerStatus?.icon} className="ml-2 size-4" />}
          </Badge>
        </div>
      </CardFooter>
    </Card>
  )
  if (hasVisibleAction && action) {
    return (
      <CardAction action={action} data={data}>
        {cardContent}
      </CardAction>
    )
  }

  if (navigation) {
    return (
      <Link
        to={`${CATALOG_BASE_PATH}${navigation.pageId}?${searchParams.toString()}`}
        className="block h-full"
        onClick={() => {
          if (navigation.filters) {
            submitFilters(navigation.filters)
          }
        }}
      >
        {cardContent}
      </Link>
    )
  }

  return cardContent
}

function ChartCardWrapper({
  children,
  clickable,
  className,
}: {
  children: React.ReactNode
  clickable?: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        'h-full rounded-full bg-[radial-gradient(circle,white_0%,white_30%,transparent_70%)] dark:bg-[radial-gradient(circle,black_0%,black_30%,transparent_70%)]',
        {
          'group-hover:bg-none': clickable,
        },
        className
      )}
    >
      {children}
    </div>
  )
}
